import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import {
    BellAlertIcon,
    ClockIcon,
    ChartBarIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    PaperAirplaneIcon,
    MoonIcon,
    DocumentTextIcon,
    BoltIcon,
    EyeIcon,
} from "@heroicons/react/20/solid";
import { Heading } from "src/components/base/heading";
import { Badge } from "src/components/base/badge";
import { Button } from "src/components/base/button";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "src/components/base/dialog";
import { Input } from "src/components/base/input";
import { Select } from "src/components/base/select";
import { Field, Label } from "src/components/base/fieldset";
import { Switch } from "src/components/base/switch";
import { MOCK_NOTIFICATION_CHANNELS } from "../data/settings";
import type { NotificationChannel } from "../data/settings";
import { Card, StatCard } from "../shared";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NotificationLogEntry {
    id: string;
    timestamp: string;
    channel: string;
    target: string;
    subject: string;
    severity: "critical" | "high" | "medium" | "low";
    status: "delivered" | "failed" | "pending";
    deliveryTime: string;
    retryCount: number;
}

interface EscalationRule {
    id: string;
    name: string;
    triggerMinutes: number;
    escalateTo: string;
    channel: string;
    severity: ("critical" | "high")[];
    active: boolean;
}

interface QuietHoursConfig {
    enabled: boolean;
    startTime: string;
    endTime: string;
    timezone: string;
    overrideForCritical: boolean;
    activeDays: string[];
}

interface NotificationTemplate {
    id: string;
    name: string;
    channel: "email" | "slack" | "webhook";
    subject: string;
    preview: string;
    lastModified: string;
}

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const MOCK_NOTIFICATION_LOG: NotificationLogEntry[] = [
    {
        id: "nl-1",
        timestamp: "2026-03-13 14:23:01",
        channel: "email",
        target: "soc-team@trufflepig.com",
        subject: "CRITICAL: Brute Force - fw-prod-01",
        severity: "critical",
        status: "delivered",
        deliveryTime: "1.2s",
        retryCount: 0,
    },
    {
        id: "nl-2",
        timestamp: "2026-03-13 14:23:01",
        channel: "slack",
        target: "#security-alerts",
        subject: "CRITICAL: Brute Force - fw-prod-01",
        severity: "critical",
        status: "delivered",
        deliveryTime: "0.8s",
        retryCount: 0,
    },
    {
        id: "nl-3",
        timestamp: "2026-03-13 14:18:33",
        channel: "email",
        target: "soc-team@trufflepig.com",
        subject: "HIGH: Malware Detected - edr-ws-142",
        severity: "high",
        status: "delivered",
        deliveryTime: "1.5s",
        retryCount: 0,
    },
    {
        id: "nl-4",
        timestamp: "2026-03-13 14:18:33",
        channel: "slack",
        target: "#security-alerts",
        subject: "HIGH: Malware Detected - edr-ws-142",
        severity: "high",
        status: "delivered",
        deliveryTime: "0.6s",
        retryCount: 0,
    },
    {
        id: "nl-5",
        timestamp: "2026-03-13 13:55:12",
        channel: "webhook",
        target: "hooks.example.com",
        subject: "Data Exfiltration - proxy-ext-02",
        severity: "high",
        status: "failed",
        deliveryTime: "—",
        retryCount: 3,
    },
    {
        id: "nl-6",
        timestamp: "2026-03-13 13:41:44",
        channel: "slack",
        target: "#security-alerts",
        subject: "MEDIUM: Suspicious Login - ad-dc-01",
        severity: "medium",
        status: "delivered",
        deliveryTime: "0.7s",
        retryCount: 0,
    },
    {
        id: "nl-7",
        timestamp: "2026-03-13 13:12:08",
        channel: "email",
        target: "soc-team@trufflepig.com",
        subject: "LOW: Port Scan - ids-dmz-03",
        severity: "low",
        status: "pending",
        deliveryTime: "—",
        retryCount: 0,
    },
    {
        id: "nl-8",
        timestamp: "2026-03-13 12:58:22",
        channel: "slack",
        target: "#security-alerts",
        subject: "CRITICAL: Malware - edr-srv-08",
        severity: "critical",
        status: "delivered",
        deliveryTime: "0.5s",
        retryCount: 0,
    },
    {
        id: "nl-9",
        timestamp: "2026-03-13 12:58:22",
        channel: "email",
        target: "management@trufflepig.com",
        subject: "CRITICAL: Malware - edr-srv-08",
        severity: "critical",
        status: "delivered",
        deliveryTime: "2.1s",
        retryCount: 0,
    },
    {
        id: "nl-10",
        timestamp: "2026-03-13 12:30:00",
        channel: "slack",
        target: "#pentest-findings",
        subject: "HIGH: OpenSSL CVE - lb-prod-02",
        severity: "high",
        status: "delivered",
        deliveryTime: "0.9s",
        retryCount: 0,
    },
];

const MOCK_ESCALATION_RULES: EscalationRule[] = [
    {
        id: "esc-1",
        name: "Critical unacked 5m",
        triggerMinutes: 5,
        escalateTo: "SOC Lead (on-call)",
        channel: "PagerDuty",
        severity: ["critical"],
        active: true,
    },
    {
        id: "esc-2",
        name: "Critical unacked 15m",
        triggerMinutes: 15,
        escalateTo: "CISO",
        channel: "Phone",
        severity: ["critical"],
        active: true,
    },
    {
        id: "esc-3",
        name: "High unacked 30m",
        triggerMinutes: 30,
        escalateTo: "SOC Lead (on-call)",
        channel: "Slack DM",
        severity: ["critical", "high"],
        active: true,
    },
    {
        id: "esc-4",
        name: "High unacked 60m",
        triggerMinutes: 60,
        escalateTo: "Management",
        channel: "Email",
        severity: ["high"],
        active: false,
    },
];

const INITIAL_QUIET_HOURS: QuietHoursConfig = {
    enabled: true,
    startTime: "22:00",
    endTime: "07:00",
    timezone: "Europe/Berlin",
    overrideForCritical: true,
    activeDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
};

const MOCK_TEMPLATES: NotificationTemplate[] = [
    {
        id: "tpl-1",
        name: "Critical Alert Email",
        channel: "email",
        subject: "[CRITICAL] {{alert.title}} - {{alert.source}}",
        preview:
            "Subject: [CRITICAL] Brute Force - fw-prod-01\n\nSeverity: CRITICAL\nSource: fw-prod-01\nTime: 14:23 CET\n\nDescription:\n185.143.223.1 brute force attack detected.\nNDR blocked lateral movement.\nZTNA session terminated.\n\n---\nTrufflePig Security Platform",
        lastModified: "2026-02-15",
    },
    {
        id: "tpl-2",
        name: "Slack Alert",
        channel: "slack",
        subject: ":rotating_light: {{severity}} | {{alert.title}}",
        preview:
            ":rotating_light: *CRITICAL* | Brute Force - fw-prod-01\n\n> 185.143.223.1 brute force attack detected.\n> NDR blocked lateral movement.\n\n*Source:* `fw-prod-01`  *Time:* 14:23 CET\n*MITRE:* TA0006 Credential Access\n\n<View in Dashboard|https://app.trufflepig.com/alert/SOC-1247>",
        lastModified: "2026-02-20",
    },
    {
        id: "tpl-3",
        name: "Webhook Payload",
        channel: "webhook",
        subject: "POST /webhook/security",
        preview:
            '{\n  "event": "alert.created",\n  "severity": "critical",\n  "alert": {\n    "id": "SOC-1247",\n    "title": "Brute Force - fw-prod-01",\n    "source": "fw-prod-01",\n    "time": "2026-03-13T14:23:01Z",\n    "mitre": "TA0006"\n  }\n}',
        lastModified: "2026-01-10",
    },
];

const severityColor = { critical: "red", high: "orange", medium: "yellow", low: "zinc" } as const;
const statusColor = { delivered: "green", failed: "red", pending: "yellow" } as const;
const statusIcon: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    delivered: CheckCircleIcon,
    failed: XCircleIcon,
    pending: ClockIcon,
};
const typeIcon: Record<string, string> = { email: "Email", slack: "Slack", webhook: "Webhook" };
const channelIcon: Record<string, string> = {
    email: "Email",
    slack: "Slack",
    webhook: "Webhook",
    PagerDuty: "PagerDuty",
    Phone: "Phone",
    "Slack DM": "Slack DM",
    Email: "Email",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionHeading({
    children,
    icon: Icon,
    description,
}: {
    children: React.ReactNode;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    description?: string;
}) {
    return (
        <div className="mb-4">
            <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-blue-500" />
                <h3 className="text-base font-semibold text-zinc-900 dark:text-white">{children}</h3>
            </div>
            {description && <p className="ml-7 mt-1 text-xs text-zinc-500 dark:text-zinc-400">{description}</p>}
        </div>
    );
}

function ToastMock({ message, onClose }: { message: string; onClose: () => void }) {
    return (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 shadow-lg dark:border-green-800/50 dark:bg-green-950/90">
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
            <span className="text-sm font-medium text-green-800 dark:text-green-300">{message}</span>
            <button onClick={onClose} className="ml-2 text-green-400 hover:text-green-600">
                <XCircleIcon className="h-4 w-4" />
            </button>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SettingsNotifications() {
    const { t } = useTranslation();
    const [channels, setChannels] = useState<NotificationChannel[]>(MOCK_NOTIFICATION_CHANNELS);
    const [showAdd, setShowAdd] = useState(false);

    // Notification log
    const [notifLog] = useState<NotificationLogEntry[]>(MOCK_NOTIFICATION_LOG);

    // Escalation rules
    const [escalationRules, setEscalationRules] = useState<EscalationRule[]>(MOCK_ESCALATION_RULES);
    const [showAddEscalation, setShowAddEscalation] = useState(false);
    const [newEscName, setNewEscName] = useState("");
    const [newEscMinutes, setNewEscMinutes] = useState("5");
    const [newEscTarget, setNewEscTarget] = useState("");
    const [newEscChannel, setNewEscChannel] = useState("Slack DM");

    // Quiet hours
    const [quietHours, setQuietHours] = useState<QuietHoursConfig>(INITIAL_QUIET_HOURS);

    // Templates
    const [templates] = useState<NotificationTemplate[]>(MOCK_TEMPLATES);
    const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);

    // Toast
    const [toast, setToast] = useState<string | null>(null);

    const toggleActive = useCallback((id: string) => {
        setChannels((prev) => prev.map((ch) => (ch.id === id ? { ...ch, active: !ch.active } : ch)));
    }, []);

    const toggleEscalation = useCallback((id: string) => {
        setEscalationRules((prev) => prev.map((r) => (r.id === id ? { ...r, active: !r.active } : r)));
    }, []);

    const sendTestNotification = useCallback((channelType: string, target: string) => {
        setToast(`Test notification sent via ${channelType} to ${target}`);
        setTimeout(() => setToast(null), 3000);
    }, []);

    const addEscalationRule = useCallback(() => {
        const newRule: EscalationRule = {
            id: `esc-${Date.now()}`,
            name: newEscName || `Rule ${escalationRules.length + 1}`,
            triggerMinutes: parseInt(newEscMinutes, 10),
            escalateTo: newEscTarget || "SOC Lead",
            channel: newEscChannel,
            severity: ["critical", "high"],
            active: true,
        };
        setEscalationRules((prev) => [...prev, newRule]);
        setShowAddEscalation(false);
        setNewEscName("");
        setNewEscTarget("");
    }, [newEscName, newEscMinutes, newEscTarget, newEscChannel, escalationRules.length]);

    const toggleQuietDay = useCallback((day: string) => {
        setQuietHours((prev) => ({
            ...prev,
            activeDays: prev.activeDays.includes(day)
                ? prev.activeDays.filter((d) => d !== day)
                : [...prev.activeDays, day],
        }));
    }, []);

    // Stats
    const stats = useMemo(() => {
        const delivered = notifLog.filter((n) => n.status === "delivered").length;
        const total = notifLog.length;
        const avgResponseTime =
            notifLog.filter((n) => n.deliveryTime !== "—").reduce((sum, n) => sum + parseFloat(n.deliveryTime), 0) /
            Math.max(delivered, 1);
        return {
            sentToday: total,
            deliveryRate: Math.round((delivered / total) * 100),
            avgResponseTime: avgResponseTime.toFixed(1) + "s",
            failedCount: notifLog.filter((n) => n.status === "failed").length,
        };
    }, [notifLog]);

    return (
        <>
            {/* Toast */}
            {toast && <ToastMock message={toast} onClose={() => setToast(null)} />}

            {/* Header */}
            <div className="flex items-center justify-between">
                <Heading>{t("settings.notifications.title")}</Heading>
                <Button color="blue" onClick={() => setShowAdd(true)}>
                    <PlusIcon className="h-4 w-4" />
                    {t("settings.notifications.add")}
                </Button>
            </div>
            <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">{t("settings.notifications.description")}</p>

            {/* ============================================================ */}
            {/* STATS                                                        */}
            {/* ============================================================ */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Sent Today" value={String(stats.sentToday)} trend="+3 from yesterday" />
                <StatCard label="Delivery Rate" value={`${stats.deliveryRate}%`} trend="99% avg this week" />
                <StatCard label="Avg Delivery Time" value={stats.avgResponseTime} trend="0.2s faster" trendDown />
                <StatCard
                    label="Failed"
                    value={String(stats.failedCount)}
                    trend={stats.failedCount > 0 ? "1 retry exhausted" : "None"}
                    trendDown={stats.failedCount === 0}
                />
            </div>

            {/* ============================================================ */}
            {/* NOTIFICATION CHANNELS                                        */}
            {/* ============================================================ */}
            <Card className="mt-6">
                <SectionHeading icon={BellAlertIcon} description="Configured notification delivery channels">
                    Notification Channels
                </SectionHeading>
                <Table dense>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{t("settings.notifications.colType")}</TableHeader>
                            <TableHeader>{t("settings.notifications.colTarget")}</TableHeader>
                            <TableHeader>{t("settings.notifications.colSeverity")}</TableHeader>
                            <TableHeader>{t("settings.notifications.colSystems")}</TableHeader>
                            <TableHeader>{t("settings.notifications.colActive")}</TableHeader>
                            <TableHeader>Test</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {channels.map((ch) => (
                            <TableRow key={ch.id}>
                                <TableCell>
                                    <Badge color="zinc">{typeIcon[ch.type]}</Badge>
                                </TableCell>
                                <TableCell className="font-mono text-xs">{ch.target}</TableCell>
                                <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                        {ch.severityFilter.map((sev) => (
                                            <Badge key={sev} color={severityColor[sev]}>
                                                {sev}
                                            </Badge>
                                        ))}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                        {ch.systemFilter.map((sys) => (
                                            <Badge key={sys} color="zinc">
                                                {sys.toUpperCase()}
                                            </Badge>
                                        ))}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Switch checked={ch.active} onChange={() => toggleActive(ch.id)} />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        plain
                                        onClick={() => sendTestNotification(ch.type, ch.target)}
                                        disabled={!ch.active}
                                    >
                                        <PaperAirplaneIcon className="h-4 w-4 text-blue-500" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* ============================================================ */}
            {/* NOTIFICATION LOG                                             */}
            {/* ============================================================ */}
            <Card className="mt-6">
                <SectionHeading icon={DocumentTextIcon} description="Last 10 notifications sent from the platform">
                    Notification Log
                </SectionHeading>
                <Table dense>
                    <TableHead>
                        <TableRow>
                            <TableHeader>Status</TableHeader>
                            <TableHeader>Time</TableHeader>
                            <TableHeader>Channel</TableHeader>
                            <TableHeader>Target</TableHeader>
                            <TableHeader>Subject</TableHeader>
                            <TableHeader>Severity</TableHeader>
                            <TableHeader>Delivery</TableHeader>
                            <TableHeader>Retries</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notifLog.map((entry) => {
                            const StatusIcon = statusIcon[entry.status];
                            return (
                                <TableRow key={entry.id}>
                                    <TableCell>
                                        <div className="flex items-center gap-1.5">
                                            <StatusIcon
                                                className={`h-4 w-4 ${entry.status === "delivered" ? "text-green-500" : entry.status === "failed" ? "text-red-500" : "text-yellow-500"}`}
                                            />
                                            <Badge color={statusColor[entry.status]}>{entry.status}</Badge>
                                        </div>
                                    </TableCell>
                                    <TableCell className="font-mono text-xs text-zinc-500">{entry.timestamp}</TableCell>
                                    <TableCell>
                                        <Badge color="zinc">{entry.channel}</Badge>
                                    </TableCell>
                                    <TableCell className="max-w-[120px] truncate font-mono text-xs">
                                        {entry.target}
                                    </TableCell>
                                    <TableCell className="max-w-[200px] truncate text-xs">{entry.subject}</TableCell>
                                    <TableCell>
                                        <Badge color={severityColor[entry.severity]}>{entry.severity}</Badge>
                                    </TableCell>
                                    <TableCell className="font-mono text-xs text-zinc-500">
                                        {entry.deliveryTime}
                                    </TableCell>
                                    <TableCell>
                                        {entry.retryCount > 0 ? (
                                            <Badge color="red">{entry.retryCount}x</Badge>
                                        ) : (
                                            <span className="text-xs text-zinc-400">—</span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Card>

            {/* ============================================================ */}
            {/* ESCALATION RULES                                             */}
            {/* ============================================================ */}
            <Card className="mt-6">
                <div className="flex items-center justify-between">
                    <SectionHeading icon={BoltIcon} description="Automatic escalation when alerts are not acknowledged">
                        Escalation Rules
                    </SectionHeading>
                    <Button color="blue" onClick={() => setShowAddEscalation(true)}>
                        <PlusIcon className="h-4 w-4" />
                        Add Rule
                    </Button>
                </div>
                <div className="space-y-3">
                    {escalationRules.map((rule) => (
                        <div
                            key={rule.id}
                            className={`flex items-center gap-4 rounded-xl border p-4 transition-all ${rule.active ? "border-zinc-200/60 dark:border-zinc-700/40" : "border-zinc-200/30 opacity-50 dark:border-zinc-700/20"}`}
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
                                <ClockIcon className="h-5 w-5 text-orange-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-zinc-900 dark:text-white">
                                        {rule.name}
                                    </span>
                                    <div className="flex gap-1">
                                        {rule.severity.map((sev) => (
                                            <Badge key={sev} color={severityColor[sev]}>
                                                {sev}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                <p className="mt-0.5 text-xs text-zinc-500">
                                    If no response in{" "}
                                    <span className="font-mono font-semibold text-zinc-700 dark:text-zinc-300">
                                        {rule.triggerMinutes} min
                                    </span>
                                    , escalate to{" "}
                                    <span className="font-semibold text-zinc-700 dark:text-zinc-300">
                                        {rule.escalateTo}
                                    </span>{" "}
                                    via
                                    <Badge color="zinc" className="ml-1">
                                        {channelIcon[rule.channel]}
                                    </Badge>
                                </p>
                            </div>
                            <Switch checked={rule.active} onChange={() => toggleEscalation(rule.id)} />
                        </div>
                    ))}
                </div>
            </Card>

            {/* ============================================================ */}
            {/* QUIET HOURS                                                  */}
            {/* ============================================================ */}
            <Card className="mt-6">
                <div className="flex items-center justify-between">
                    <SectionHeading icon={MoonIcon} description="Suppress non-critical notifications during off hours">
                        Quiet Hours
                    </SectionHeading>
                    <Switch
                        checked={quietHours.enabled}
                        onChange={(checked) => setQuietHours((prev) => ({ ...prev, enabled: checked }))}
                    />
                </div>
                {quietHours.enabled && (
                    <div className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-3">
                            <Field>
                                <Label>Start Time</Label>
                                <Input
                                    type="time"
                                    value={quietHours.startTime}
                                    onChange={(e) => setQuietHours((prev) => ({ ...prev, startTime: e.target.value }))}
                                />
                            </Field>
                            <Field>
                                <Label>End Time</Label>
                                <Input
                                    type="time"
                                    value={quietHours.endTime}
                                    onChange={(e) => setQuietHours((prev) => ({ ...prev, endTime: e.target.value }))}
                                />
                            </Field>
                            <Field>
                                <Label>Timezone</Label>
                                <Select
                                    value={quietHours.timezone}
                                    onChange={(e) => setQuietHours((prev) => ({ ...prev, timezone: e.target.value }))}
                                >
                                    <option value="Europe/Berlin">Europe/Berlin (CET)</option>
                                    <option value="Europe/London">Europe/London (GMT)</option>
                                    <option value="America/New_York">America/New York (EST)</option>
                                    <option value="UTC">UTC</option>
                                </Select>
                            </Field>
                        </div>

                        {/* Active days */}
                        <div>
                            <p className="mb-2 text-xs font-medium text-zinc-500">Active Days</p>
                            <div className="flex gap-2">
                                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                                    <button
                                        key={day}
                                        onClick={() => toggleQuietDay(day)}
                                        className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                                            quietHours.activeDays.includes(day)
                                                ? "bg-blue-500 text-white shadow-sm"
                                                : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
                                        }`}
                                    >
                                        {day}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Override for critical */}
                        <div className="flex items-center justify-between rounded-lg border border-red-200/60 bg-red-50/30 p-3 dark:border-red-900/40 dark:bg-red-950/10">
                            <div className="flex items-center gap-2">
                                <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                                <span className="text-sm text-zinc-700 dark:text-zinc-300">
                                    Override quiet hours for critical alerts
                                </span>
                            </div>
                            <Switch
                                checked={quietHours.overrideForCritical}
                                onChange={(checked) =>
                                    setQuietHours((prev) => ({ ...prev, overrideForCritical: checked }))
                                }
                            />
                        </div>

                        {/* Visual timeline */}
                        <div>
                            <p className="mb-2 text-xs font-medium text-zinc-500">Quiet Window Preview</p>
                            <div className="relative h-8 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                                {(() => {
                                    const startHour = parseInt(quietHours.startTime.split(":")[0], 10);
                                    const endHour = parseInt(quietHours.endTime.split(":")[0], 10);
                                    const startPct = (startHour / 24) * 100;
                                    const endPct = (endHour / 24) * 100;
                                    if (startHour > endHour) {
                                        // wraps midnight
                                        return (
                                            <>
                                                <div
                                                    className="absolute inset-y-0 rounded-full bg-indigo-200/60 dark:bg-indigo-800/40"
                                                    style={{ left: `${startPct}%`, right: 0 }}
                                                />
                                                <div
                                                    className="absolute inset-y-0 rounded-full bg-indigo-200/60 dark:bg-indigo-800/40"
                                                    style={{ left: 0, width: `${endPct}%` }}
                                                />
                                            </>
                                        );
                                    }
                                    return (
                                        <div
                                            className="absolute inset-y-0 rounded-full bg-indigo-200/60 dark:bg-indigo-800/40"
                                            style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
                                        />
                                    );
                                })()}
                                <div className="absolute inset-0 flex items-center justify-between px-3 font-mono text-[10px] text-zinc-400">
                                    <span>00:00</span>
                                    <span>06:00</span>
                                    <span>12:00</span>
                                    <span>18:00</span>
                                    <span>24:00</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* ============================================================ */}
            {/* NOTIFICATION TEMPLATES                                       */}
            {/* ============================================================ */}
            <Card className="mt-6">
                <SectionHeading icon={DocumentTextIcon} description="Preview the format of outgoing notifications">
                    Notification Templates
                </SectionHeading>
                <div className="space-y-3">
                    {templates.map((tpl) => (
                        <div
                            key={tpl.id}
                            className="rounded-xl border border-zinc-200/60 transition-all dark:border-zinc-700/40"
                        >
                            <button
                                className="flex w-full items-center justify-between p-4 text-left"
                                onClick={() => setExpandedTemplate(expandedTemplate === tpl.id ? null : tpl.id)}
                            >
                                <div className="flex items-center gap-3">
                                    <Badge
                                        color={
                                            tpl.channel === "email"
                                                ? "blue"
                                                : tpl.channel === "slack"
                                                  ? "purple"
                                                  : "orange"
                                        }
                                    >
                                        {tpl.channel}
                                    </Badge>
                                    <span className="text-sm font-medium text-zinc-900 dark:text-white">
                                        {tpl.name}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-zinc-400">Modified {tpl.lastModified}</span>
                                    <EyeIcon className="h-4 w-4 text-zinc-400" />
                                </div>
                            </button>
                            {expandedTemplate === tpl.id && (
                                <div className="border-t border-zinc-200/60 p-4 dark:border-zinc-700/40">
                                    <p className="mb-2 text-xs font-medium text-zinc-500">Subject / Title:</p>
                                    <p className="mb-3 font-mono text-xs text-zinc-700 dark:text-zinc-300">
                                        {tpl.subject}
                                    </p>
                                    <p className="mb-2 text-xs font-medium text-zinc-500">Preview:</p>
                                    <pre className="overflow-x-auto rounded-lg bg-zinc-50 p-3 font-mono text-xs leading-relaxed text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
                                        {tpl.preview}
                                    </pre>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Card>

            {/* ============================================================ */}
            {/* DIALOGS                                                      */}
            {/* ============================================================ */}

            {/* Add channel dialog */}
            <Dialog open={showAdd} onClose={() => setShowAdd(false)} size="lg">
                <DialogTitle>{t("settings.notifications.addTitle")}</DialogTitle>
                <DialogBody>
                    <div className="space-y-4">
                        <Field>
                            <Label>{t("settings.notifications.colType")}</Label>
                            <Select>
                                <option value="email">Email</option>
                                <option value="slack">Slack</option>
                                <option value="webhook">Webhook</option>
                            </Select>
                        </Field>
                        <Field>
                            <Label>{t("settings.notifications.colTarget")}</Label>
                            <Input placeholder={t("settings.notifications.targetPlaceholder")} />
                        </Field>
                        <Field>
                            <Label>Severity Filter</Label>
                            <div className="mt-1 flex gap-2">
                                {(["critical", "high", "medium", "low"] as const).map((sev) => (
                                    <label
                                        key={sev}
                                        className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-zinc-200 px-2.5 py-1.5 text-xs transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
                                    >
                                        <input
                                            type="checkbox"
                                            className="rounded"
                                            defaultChecked={sev === "critical" || sev === "high"}
                                        />
                                        <Badge color={severityColor[sev]}>{sev}</Badge>
                                    </label>
                                ))}
                            </div>
                        </Field>
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setShowAdd(false)}>
                        {t("tickets.cancel")}
                    </Button>
                    <Button color="blue" onClick={() => setShowAdd(false)}>
                        {t("tickets.submit")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add escalation rule dialog */}
            <Dialog open={showAddEscalation} onClose={() => setShowAddEscalation(false)} size="lg">
                <DialogTitle>Add Escalation Rule</DialogTitle>
                <DialogBody>
                    <div className="space-y-4">
                        <Field>
                            <Label>Rule Name</Label>
                            <Input
                                value={newEscName}
                                onChange={(e) => setNewEscName(e.target.value)}
                                placeholder="e.g. Critical unacked 5m"
                            />
                        </Field>
                        <div className="grid grid-cols-2 gap-4">
                            <Field>
                                <Label>Trigger After (minutes)</Label>
                                <Select value={newEscMinutes} onChange={(e) => setNewEscMinutes(e.target.value)}>
                                    <option value="5">5 minutes</option>
                                    <option value="10">10 minutes</option>
                                    <option value="15">15 minutes</option>
                                    <option value="30">30 minutes</option>
                                    <option value="60">60 minutes</option>
                                    <option value="120">2 hours</option>
                                </Select>
                            </Field>
                            <Field>
                                <Label>Escalation Channel</Label>
                                <Select value={newEscChannel} onChange={(e) => setNewEscChannel(e.target.value)}>
                                    <option value="Slack DM">Slack DM</option>
                                    <option value="Email">Email</option>
                                    <option value="PagerDuty">PagerDuty</option>
                                    <option value="Phone">Phone Call</option>
                                </Select>
                            </Field>
                        </div>
                        <Field>
                            <Label>Escalate To</Label>
                            <Input
                                value={newEscTarget}
                                onChange={(e) => setNewEscTarget(e.target.value)}
                                placeholder="e.g. SOC Lead (on-call)"
                            />
                        </Field>
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setShowAddEscalation(false)}>
                        Cancel
                    </Button>
                    <Button color="blue" onClick={addEscalationRule}>
                        Add Rule
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
